import { MassUnits } from "@/libs/units/mass";
import { PercentageUnits } from "@/libs/units/percentage";
import { PowerUnits } from "@/libs/units/power";
import { EquipmentCalculatorSectionKey } from "../../calculationTypes";
import type { ConverterInputProps } from "@/components/types";
import { VolumeUnits } from "@/libs/units/volume";
import type { Section, SectionDeps } from "../types";
import { ScenarioDetailSectionKey, ScenarioDetailSectionTitle } from "../types";

export const buildSealSideCalculatorSection = ({
  scenarioCalculations,
  getHydraulicPowerUnit,
  getVolumeUnit
}: SectionDeps): Section => ({
  id: EquipmentCalculatorSectionKey.SEAL,
  hash: ScenarioDetailSectionKey.SEAL_SENSOR_MLE,
  label: ScenarioDetailSectionTitle.SEAL_SENSOR_MLE,
  routeName: "SizingParameters",
  aggregateDisplays: [
    {
      label: "HP",
      base: PowerUnits.HorsePower,
      target: getHydraulicPowerUnit().value,
      model: PowerUnits.options(),
      value:
        scenarioCalculations.sealCalculations.aggregate.sealOperatingHP.value,
      mode: "read"
    },
    {
      label: "Thrust Load",
      base: MassUnits.Pounds,
      model: MassUnits.options(),
      value:
        scenarioCalculations.pumpCalculations.aggregate.thrustLoadPounds.value,
      mode: "convert"
    },
    {
      label: "Shut-in Thrust Load",
      base: MassUnits.Pounds,
      model: MassUnits.options(),
      value:
        scenarioCalculations.pumpCalculations.aggregate.shutInThrustLoadPounds
          .value,
      mode: "convert"
    },
    {
      label: "Shut-in Bearing Load Ratio",
      base: PercentageUnits.Ratio,
      target: PercentageUnits.Percentage,
      model: PercentageUnits.options(),
      value:
        scenarioCalculations.sealCalculations.aggregate
          .sealShutInBearingLoadRatio.value,
      mode: "read"
    },
    {
      label: "Total Seal Volume",
      base: VolumeUnits.Gallons,
      target: getVolumeUnit().value,
      model: VolumeUnits.options(),
      value:
        scenarioCalculations.sealCalculations.aggregate.sealTotalVolumeGallons
          .value,
      mode: "read"
    },
    {
      label: "Upper Chamber % Upon Pull",
      base: PercentageUnits.Ratio,
      target: PercentageUnits.Percentage,
      model: PercentageUnits.options(),
      value:
        scenarioCalculations.sealCalculations.aggregate
          .sealUpperChamberPullExpansionRatio.value,
      mode: "read"
    }
  ],
  individualDisplays: scenarioCalculations.descendingOrderSeals.value.map(
    (xrefSeal, index) => {
      const calcs =
        scenarioCalculations.sealCalculations.individual.value[xrefSeal?.id];
      // core displays for every seal
      const displays: ConverterInputProps[] = [
        {
          label: "Operational Flex",
          base: VolumeUnits.Gallons,
          target: getVolumeUnit().value,
          model: VolumeUnits.options(),
          mode: "convert",
          value: calcs.operationalTotalAmountOfExpansionGal
        },
        {
          label: "Operational Capacity",
          base: VolumeUnits.Gallons,
          target: getVolumeUnit().value,
          model: VolumeUnits.options(),
          mode: "convert",
          value: calcs.operationalTotalExpansionGal
        },
        {
          label: "Pull Flex",
          base: VolumeUnits.Gallons,
          target: getVolumeUnit().value,
          model: VolumeUnits.options(),
          mode: "convert",
          value: calcs.pullTotalAmountOfExpansionGal
        },
        {
          label: "Pull Capacity",
          base: VolumeUnits.Gallons,
          target: getVolumeUnit().value,
          model: VolumeUnits.options(),
          mode: "convert",
          value: calcs.pullTotalExpansionGal
        },
        {
          label: "% Expansion during Install",
          base: PercentageUnits.Ratio,
          target: PercentageUnits.Percentage,
          model: PercentageUnits.options(),
          value: calcs.installExpansionRatio,
          mode: "read"
        },
        {
          label: "% Expansion per Cycle",
          base: PercentageUnits.Ratio,
          target: PercentageUnits.Percentage,
          model: PercentageUnits.options(),
          value: calcs.operationExpansionRatio,
          mode: "read"
        },
        {
          label: "% Expansion upon Pull",
          base: PercentageUnits.Ratio,
          target: PercentageUnits.Percentage,
          model: PercentageUnits.options(),
          value: calcs.pullExpansionRatio,
          mode: "read"
        },
        {
          label: "Shaft Load %",
          base: PercentageUnits.Ratio,
          target: PercentageUnits.Percentage,
          model: PercentageUnits.options(),
          value: calcs.xrefSeal?.use_high_strength_shaft
            ? calcs.highStrengthShaftRatingRatio
            : calcs.standardShaftRatingRatio,
          mode: "read"
        },
        {
          label: "Bearing Rating @ 3500",
          base: MassUnits.Pounds,
          model: MassUnits.options(),
          value: calcs.bearingRatingAt60hzLBS,
          mode: "convert"
        },
        {
          label: "Bearing Rating @ RPM",
          base: MassUnits.Pounds,
          model: MassUnits.options(),
          value: calcs.bearingRatingAtRPMLBS,
          mode: "convert"
        }
      ];

      // add Bearing Load Ratio only for the first seal in the list
      if (index === 0) {
        displays.push({
          label: "Bearing Load Ratio",
          base: PercentageUnits.Ratio,
          target: PercentageUnits.Percentage,
          model: PercentageUnits.options(),
          value: calcs.bearingLoadRatio,
          mode: "read"
        });
      }

      return {
        label: calcs.xrefSeal?.r_seal?.description || "",
        displays
      };
    }
  )
});
